import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Empty,
  List,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { AUTH_USER } from "../../../store/selectors";
import { AuditLogEntry } from "../../../services/audit/audit.types";
import { TrashEntity } from "../../../services/trash/trash.types";
import {
  ContentHealthSummaryResponse,
  ProgramHealthSummary,
} from "../../../services/content-health/content-health.types";
import {
  CONTENT_HEALTH_STATUS_ORDER,
  CONTENT_HEALTH_STATUS_TAGS,
  PLATFORM_STATUS_TAGS,
  resolveAuditActionTag,
} from "../../../constants/status-colors";
import { getSchools } from "../../../services/schools/school.service";
import { CardError, CardLoading } from "./CardStates";
import {
  ActivityState,
  HealthState,
  PlatformStatusState,
  TRASH_DAYS_WINDOW,
  TRASH_ENTITIES,
  TrashState,
  sumHealthIncidents,
  useDashboardData,
} from "./useDashboardData";

const { Text, Title } = Typography;

// -----------------------------------------------------------------------
// KPI row
// -----------------------------------------------------------------------

interface KpiCardProps {
  title: string;
  // null means the source failed or has no data yet → render "—".
  value: number | null;
  loading: boolean;
  to: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, loading, to }) => (
  <Link to={to} style={{ display: "block" }}>
    <Card hoverable size="small">
      <Statistic title={title} value={value ?? "—"} loading={loading} />
    </Card>
  </Link>
);

// -----------------------------------------------------------------------
// Recent activity card
// -----------------------------------------------------------------------

interface RecentActivityCardProps {
  activity: ActivityState;
  onRetry: () => void;
}

const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  activity,
  onRetry,
}) => (
  <Card
    title="Actividad reciente"
    actions={[
      <Link key="link" to="/actividad">
        Ver toda la actividad
      </Link>,
    ]}
  >
    {activity.loading ? (
      <CardLoading />
    ) : activity.error ? (
      <CardError
        message="No se pudo cargar la actividad reciente."
        onRetry={onRetry}
      />
    ) : activity.logs.length === 0 ? (
      <Empty description="Sin actividad reciente." />
    ) : (
      <List
        size="small"
        dataSource={activity.logs}
        renderItem={(log: AuditLogEntry) => {
          const actionTag = resolveAuditActionTag(log.action);
          return (
          <List.Item key={log.id}>
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              <Space size="small">
                <Tag color={actionTag.color}>{actionTag.label}</Tag>
                <Text>{log.actor_name}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {log.entity_type
                  ? `${log.entity_type}${
                      log.entity_id !== null ? ` #${log.entity_id}` : ""
                    }`
                  : "—"}
                {" · "}
                {dayjs(log.created_at).format("DD/MM HH:mm")}
              </Text>
            </Space>
          </List.Item>
          );
        }}
      />
    )}
  </Card>
);

// -----------------------------------------------------------------------
// Trash summary card
// -----------------------------------------------------------------------

interface TrashSummaryCardProps {
  trash: TrashState;
  onRetry: () => void;
}

const TrashSummaryCard: React.FC<TrashSummaryCardProps> = ({
  trash,
  onRetry,
}) => {
  const { totals, total, loading, error } = trash;

  return (
    <Card
      title={`Papelera · últimos ${TRASH_DAYS_WINDOW} días`}
      actions={[
        <Link key="link" to="/papelera">
          Ir a la papelera
        </Link>,
      ]}
    >
      {loading ? (
        <CardLoading />
      ) : error ? (
        <CardError
          message="No se pudo cargar el resumen de la papelera."
          onRetry={onRetry}
        />
      ) : !totals ||
        (total === 0 &&
          Object.values(totals).every((count) => count !== null)) ? (
        <Empty
          description={`Sin elementos eliminados en los últimos ${TRASH_DAYS_WINDOW} días.`}
        />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          {TRASH_ENTITIES.map(({ entity, label }: { entity: TrashEntity; label: string }) => (
            <div
              key={entity}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Text>{label}</Text>
              <Text strong>{totals[entity] ?? "—"}</Text>
            </div>
          ))}
          <Divider style={{ margin: "4px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Total</Text>
            <Text strong style={{ fontSize: 16 }}>
              {total}
            </Text>
          </div>
        </Space>
      )}
    </Card>
  );
};

// -----------------------------------------------------------------------
// Content health card
// -----------------------------------------------------------------------

const MAX_PROGRAMS_SHOWN = 6;

// Failed programs surface too: not being able to analyze a program is
// itself a health signal, not something to hide.
const filterProgramsWithIssues = (
  summary: ContentHealthSummaryResponse | null
): ProgramHealthSummary[] =>
  (summary?.programs ?? []).filter(
    (program) => program.issues > 0 || program.failed
  );

interface ContentHealthCardProps {
  health: HealthState;
  onRetry: () => void;
  onRefresh: () => void;
}

const ContentHealthCard: React.FC<ContentHealthCardProps> = ({
  health,
  onRetry,
  onRefresh,
}) => {
  const { summary, loading, refreshing, error } = health;
  const programsWithIssues = filterProgramsWithIssues(summary).slice(
    0,
    MAX_PROGRAMS_SHOWN
  );

  return (
    <Card
      title="Salud del contenido"
      extra={
        summary && (
          <Space size="small">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {`Actualizado: ${dayjs(summary.generated_at).format("HH:mm")}`}
            </Text>
            <Button size="small" loading={refreshing} onClick={onRefresh}>
              Actualizar
            </Button>
          </Space>
        )
      }
      actions={[
        <Link key="link" to="/mapa-contenido">
          Abrir el mapa de contenido
        </Link>,
        // Direct jump to the flat work queue of unhealthy items, carrying
        // the same count the "Incidencias de salud" KPI shows.
        <Link key="attention" to="/atencion">
          {summary
            ? `Necesita atención (${sumHealthIncidents(summary.programs)})`
            : "Necesita atención"}
        </Link>,
      ]}
    >
      {loading ? (
        <CardLoading tip="Analizando programas…" />
      ) : error ? (
        <CardError
          message="No se pudo cargar la salud del contenido."
          onRetry={onRetry}
        />
      ) : programsWithIssues.length === 0 ? (
        <Empty description="Sin problemas detectados." />
      ) : (
        <List
          size="small"
          dataSource={programsWithIssues}
          renderItem={(program) => (
            <List.Item key={program.id}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text strong>{program.title}</Text>
                <Space size={[4, 4]} wrap>
                  {program.failed && <Tag>No se pudo analizar</Tag>}
                  {CONTENT_HEALTH_STATUS_ORDER.filter(
                    (key) => program[key] > 0
                  ).map((key) => (
                    <Tag key={key} color={CONTENT_HEALTH_STATUS_TAGS[key].color}>
                      {CONTENT_HEALTH_STATUS_TAGS[key].label}: {program[key]}
                    </Tag>
                  ))}
                </Space>
              </Space>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

// -----------------------------------------------------------------------
// Platform status card
// -----------------------------------------------------------------------

interface PlatformStatusCardProps {
  platform: PlatformStatusState;
  onRetry: () => void;
}

const PlatformStatusCard: React.FC<PlatformStatusCardProps> = ({
  platform,
  onRetry,
}) => {
  const { status, loading, error } = platform;
  const maintenance = status?.maintenance_mode ?? false;
  // Memoized so the lazy schools fetch below doesn't re-run on every render.
  const allowedSchoolIds = useMemo(
    () => status?.allowed_school_ids ?? [],
    [status]
  );

  // Resolved allowed-school names; the backend only returns ids, so we cross
  // them against the schools list. Fetched lazily (only in maintenance mode
  // with a non-empty list) to avoid a schools request on every dashboard load.
  const [schoolNamesById, setSchoolNamesById] = useState<Map<
    number,
    string
  > | null>(null);

  useEffect(() => {
    if (!maintenance || allowedSchoolIds.length === 0) {
      setSchoolNamesById(null);
      return;
    }
    let active = true;
    getSchools()
      .then((schools) => {
        if (!active) return;
        setSchoolNamesById(
          new Map(schools.map((school) => [school.id, school.name]))
        );
      })
      .catch(() => {
        // Fall back to "Escuela #id"; resolving the names is best-effort.
        if (active) setSchoolNamesById(null);
      });
    return () => {
      active = false;
    };
  }, [maintenance, allowedSchoolIds]);

  const schoolsLabel =
    allowedSchoolIds.length === 0
      ? "Ninguna escuela exenta"
      : allowedSchoolIds
          .map((id) => schoolNamesById?.get(id) ?? `Escuela #${id}`)
          .join(", ");

  const tag = maintenance
    ? PLATFORM_STATUS_TAGS.maintenance
    : PLATFORM_STATUS_TAGS.operational;

  return (
    <Card
      title="Estado de la plataforma"
      actions={[
        <Link key="link" to="/settings">
          Ir a configuración
        </Link>,
      ]}
    >
      {loading ? (
        <CardLoading />
      ) : error ? (
        <CardError
          message="No se pudo cargar el estado de la plataforma."
          onRetry={onRetry}
        />
      ) : !status ? (
        <Empty description="Sin información de estado." />
      ) : (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Tag color={tag.color}>{tag.label}</Tag>
          {maintenance && (
            <>
              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Text type="secondary">Escuelas con acceso</Text>
                <Text>{schoolsLabel}</Text>
              </div>
              {status.message && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {status.message}
                </Text>
              )}
            </>
          )}
        </Space>
      )}
    </Card>
  );
};

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------

const Dashboard: React.FC = () => {
  const user = useSelector(AUTH_USER);
  const {
    activity,
    trash,
    health,
    platform,
    refetchActivity,
    refetchTrash,
    refetchHealth,
    refetchPlatformStatus,
  } = useDashboardData();

  const greeting = user?.first_name ? `Hola, ${user.first_name}` : "Hola";
  const todayLabel = dayjs().locale("es").format("dddd D [de] MMMM");

  // KPI derivations: a failed source renders "—" (null), never breaks the row.
  const healthPrograms = health.error ? null : health.summary?.programs ?? null;
  const programsWithIssuesCount = healthPrograms
    ? filterProgramsWithIssues(health.summary).length
    : null;
  const healthIncidentsCount = healthPrograms
    ? sumHealthIncidents(healthPrograms)
    : null;

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <Title level={2} style={{ marginBottom: 0 }}>
          {greeting}
        </Title>
        <Text type="secondary">{todayLabel}</Text>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={12} xl={6}>
          <KpiCard
            title="Acciones recientes"
            value={activity.error ? null : activity.total}
            loading={activity.loading}
            to="/actividad"
          />
        </Col>
        <Col xs={12} md={12} xl={6}>
          <KpiCard
            title={`En papelera · ${TRASH_DAYS_WINDOW} días`}
            value={trash.error ? null : trash.total}
            loading={trash.loading}
            to="/papelera"
          />
        </Col>
        <Col xs={12} md={12} xl={6}>
          <KpiCard
            title="Programas con problemas"
            value={programsWithIssuesCount}
            loading={health.loading}
            to="/mapa-contenido"
          />
        </Col>
        <Col xs={12} md={12} xl={6}>
          <KpiCard
            title="Incidencias de salud"
            value={healthIncidentsCount}
            loading={health.loading}
            to="/mapa-contenido"
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <PlatformStatusCard
            platform={platform}
            onRetry={refetchPlatformStatus}
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <RecentActivityCard activity={activity} onRetry={refetchActivity} />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <TrashSummaryCard trash={trash} onRetry={refetchTrash} />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <ContentHealthCard
            health={health}
            onRetry={() => refetchHealth()}
            onRefresh={() => refetchHealth(true)}
          />
        </Col>
      </Row>
    </Space>
  );
};

export default Dashboard;
